import { studioDayKey } from './bookingStates';

/**
 * D10-02 (staff leave, re-scoped by Anas 7 Oct): leave is entered on the
 * artist's profile and capped by their annual allowance; a booking can't land
 * on a day the artist is on leave. ONE set of rules for the web form, the
 * calendar day-off modal, PUT /api/artists and the bookings validator (which
 * the hold / create-and-pay / reschedule / move intents all pass through).
 *
 * All days are studio (Melbourne) days, `YYYY-MM-DD`.
 */

export interface LeaveBlockLike {
  startDate: string;
  endDate: string;
  type: string;
}

export interface AvailabilityLike {
  pattern?: Record<string, { startTime: string; endTime: string } | null> | null;
  rdoDates?: readonly string[] | null;
  leaveBlocks?: readonly LeaveBlockLike[] | null;
}

/** Leave that draws down the paid-leave allowance (RDO and comp days don't; L7, 17 Jul). */
export const ALLOWANCE_LEAVE_TYPES: ReadonlySet<string> = new Set(['annual', 'time-off', 'flex-swap']);

const DOW = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const DAY_MS = 86_400_000;

const dayOf = (d: string) => (/^\d{4}-\d{2}-\d{2}$/.test(d) ? d : studioDayKey(d));
const msOf = (key: string) => Date.parse(`${key}T00:00:00Z`);
const keyOf = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** Every studio day a block covers, start and end inclusive. */
export function leaveBlockDayKeys(block: Pick<LeaveBlockLike, 'startDate' | 'endDate'>): string[] {
  const s = msOf(dayOf(block.startDate));
  const e = msOf(dayOf(block.endDate));
  const out: string[] = [];
  for (let t = s; t <= e && out.length < 400; t += DAY_MS) out.push(keyOf(t));
  return out;
}

/**
 * Is the artist off on this studio day by a DATED day off: a leave block of any
 * kind (annual, time-off, flex-swap, RDO, comp) or a legacy RDO date? The weekly
 * pattern's rest days are not "on leave" (an artist may pick up an extra day).
 */
export function isArtistOffOn(av: AvailabilityLike | null | undefined, day: string): boolean {
  if (!av) return false;
  const key = dayOf(day);
  if ((av.leaveBlocks ?? []).some((b) => key >= dayOf(b.startDate) && key <= dayOf(b.endDate))) return true;
  return (av.rdoDates ?? []).some((d) => dayOf(d) === key);
}

/** Is the studio day a working day on the artist's weekly pattern? No pattern = every day works. */
function patternWorks(av: AvailabilityLike, key: string): boolean {
  if (!av.pattern) return true;
  return Boolean(av.pattern[DOW[new Date(msOf(key)).getUTCDay()]!]);
}

/** Working days (on the weekly pattern) a leave block covers: what it costs the allowance. */
export function leaveWorkingDays(av: AvailabilityLike, block: Pick<LeaveBlockLike, 'startDate' | 'endDate'>): string[] {
  return leaveBlockDayKeys(block).filter((k) => patternWorks(av, k));
}

/**
 * Allowance-drawing working days used per calendar year ("2026" → 9). A day
 * two blocks share is counted once.
 */
export function leaveUsedByYear(av: AvailabilityLike | null | undefined): Map<string, number> {
  const days = new Set<string>();
  for (const b of av?.leaveBlocks ?? []) {
    if (!ALLOWANCE_LEAVE_TYPES.has(b.type)) continue;
    for (const k of leaveWorkingDays(av!, b)) days.add(k);
  }
  const out = new Map<string, number>();
  for (const k of days) out.set(k.slice(0, 4), (out.get(k.slice(0, 4)) ?? 0) + 1);
  return out;
}

export type CapLeaveResult =
  | { ok: true; workingDays: number }
  | { ok: false; code: 'leave-over-allowance'; year: string; workingDays: number; remaining: number; message: string };

/**
 * Would adding `block` take the artist past their annual allowance in any year?
 * Counts working days only (rostered days off are free) and only the leave
 * types that draw the allowance. No allowance set = no cap.
 */
export function capLeave(
  av: AvailabilityLike,
  allowanceDays: number | null | undefined,
  block: LeaveBlockLike,
): CapLeaveResult {
  const working = ALLOWANCE_LEAVE_TYPES.has(block.type) ? leaveWorkingDays(av, block) : [];
  if (allowanceDays == null || working.length === 0) return { ok: true, workingDays: working.length };
  const used = leaveUsedByYear(av);
  const already = new Set<string>();
  for (const b of av.leaveBlocks ?? []) {
    if (ALLOWANCE_LEAVE_TYPES.has(b.type)) for (const k of leaveWorkingDays(av, b)) already.add(k);
  }
  const addByYear = new Map<string, number>();
  for (const k of working) {
    if (already.has(k)) continue;
    addByYear.set(k.slice(0, 4), (addByYear.get(k.slice(0, 4)) ?? 0) + 1);
  }
  for (const [year, add] of [...addByYear].sort()) {
    const remaining = Math.max(0, allowanceDays - (used.get(year) ?? 0));
    if (add > remaining) {
      return {
        ok: false,
        code: 'leave-over-allowance',
        year,
        workingDays: add,
        remaining,
        message: `That's ${add} working day${add === 1 ? '' : 's'} of leave in ${year}, but only ${remaining} ${remaining === 1 ? 'is' : 'are'} left of the ${allowanceDays}-day allowance.`,
      };
    }
  }
  return { ok: true, workingDays: working.length };
}

/**
 * Server-side cap for a whole-record artist save (PUT /api/artists): refused when
 * a year ends up over the allowance AND higher than it was before this save, so
 * a record already over (seeded, or the allowance lowered) can still be edited
 * as long as the edit adds no allowance-drawing leave.
 */
export function leaveCapViolation(
  prev: AvailabilityLike | null | undefined,
  next: AvailabilityLike,
  allowanceDays: number | null | undefined,
): { year: string; used: number; allowance: number } | null {
  if (allowanceDays == null) return null;
  const before = leaveUsedByYear(prev);
  for (const [year, used] of [...leaveUsedByYear(next)].sort()) {
    if (used > allowanceDays && used > (before.get(year) ?? 0)) return { year, used, allowance: allowanceDays };
  }
  return null;
}
